import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import { Autocomplete, Grid, MenuItem, Paper, TextField, Typography, styled } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { debounce } from 'lodash';
import { useLazyQuery } from '@apollo/client';
import SingleUser from './SingleUser';
import { useAppSelector } from '../../../redux/hooks';
import { SEARCH_CLIENTS } from '../../../service/graphql/chatQuery';
import { IClientNameListItem } from '../../../types/client/IClientNameListItem';
import { ISearchClientNameListInput } from '../../../types/client/ISearchClientNameListInput';

const StyledMenuItem = styled(MenuItem)(() => ({
  padding: '12px',
  gap: '4px',
}));

const CustomTextField = styled(TextField)(() => ({
  '& .MuiFilledInput-root': {
    border: '1px solid rgba(124, 174, 190, 0.50) !important',
    backgroundColor: '#FFF',
    borderRadius: '10px',
    '&:hover': {
      backgroundColor: '#FFF',
      border: '1px solid rgba(124, 174, 190, 0.50) !important',
    },
    '&.Mui-focused': {
      backgroundColor: '#FFF',
      boxShadow: 'none',
      border: '1px solid rgba(124, 174, 190, 0.50) !important',
    },
    '&.MuiFilledInput-underline:before': {
      borderBottom: 'none !important',
    },
    '&.MuiFilledInput-underline:after': {
      borderBottom: 'none !important',
    },
    padding: '8px 12px',
  },
  '& .MuiAutocomplete-inputRoot': {
    alignItems: 'center',
  },
}));

interface CustomPaperProps {
  children: ReactNode;
}

const CustomPaperComponent: React.FC<CustomPaperProps> = ({ children }) => (
  <Paper
    style={{
      border: '1px solid rgba(124, 174, 190, 0.50)',
      borderRadius: '10px',
      marginTop: '4px',
      scrollbarColor: '#7CAEBE #fff',
    }}
  >
    {children}
  </Paper>
);

interface IUserSearchBarProps {
  handleClientClick: (_client: IClientNameListItem) => void;
}

function UserSearchBar({ handleClientClick }: IUserSearchBarProps) {
  const [searchText, setSearchText] = useState<string>('');
  const { t } = useTranslation();
  const coachId = useAppSelector((state) => state.coach.coach?.id);

  const [searchClientName, { loading, data: clients, stopPolling }] = useLazyQuery<
    { searchClientNameList: IClientNameListItem[] },
    ISearchClientNameListInput
  >(SEARCH_CLIENTS);

  useEffect(() => {
    if (!coachId) return;
    searchClientName({
      variables: {
        query: '',
        skip: 0,
        take: 15,
      },
    });
  }, [coachId]);

  const debouncedFetchClients = useCallback(
    debounce(({ nameText }: { nameText: string }) => {
      if (!coachId) return;
      searchClientName({
        variables: {
          skip: 0,
          take: 15,
          query: nameText,
        },
      });
    }, 500),
    [coachId]
  );

  useEffect(
    () => () => {
      debouncedFetchClients.cancel();
      stopPolling();
    },
    [debouncedFetchClients]
  );

  const searchHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
    debouncedFetchClients({ nameText: event.target.value });
  };

  return (
    <Grid gap="4px" display="flex" flexDirection="row" alignItems="center" width="100%">
      <Typography variant="bodyBold" color="primary" whiteSpace="nowrap">
        {`${t('chat.to')} :`}
      </Typography>
      <Autocomplete
        id="asynchronous-demo"
        fullWidth
        loading={loading}
        filterOptions={(x) => x}
        options={clients?.searchClientNameList ?? []}
        renderOption={(props, option) => (
          // <Box component="li" sx={{ '& > img': { flexShrink: 0 } }} {...props}>
          <StyledMenuItem key={option.id} onClick={() => handleClientClick(option)}>
            <SingleUser
              profilePic={option.profilePic ?? ''}
              firstName={option.firstName}
              lastName={option.lastName ?? ''}
              hideStatus
            />
            <Typography variant="bodyRegular" color="primary">
              {option.firstName} {option.lastName}
            </Typography>
          </StyledMenuItem>
          // </Box>
        )}
        renderInput={(params) => (
          <CustomTextField
            {...params}
            onChange={searchHandler}
            value={searchText}
            variant="filled"
            InputLabelProps={{ shrink: false }}
            placeholder={`${t('chat.enterName')}...`}
            inputProps={{
              ...params.inputProps,
              autoComplete: 'new-password', // disable autocomplete and autofill
              style: { padding: '0px' },
            }}
          />
        )}
        PaperComponent={CustomPaperComponent as React.ComponentType}
      />
    </Grid>
  );
}

export default UserSearchBar;
